<template>
    <div id="__layout__content" class="flex flex-col min-h-screen relative z-0">
        <Header class="z-10" />
        <div class="relative flex-grow pt-16 lg:pt-20 xl:pt-32 3xl:pt-40 z-0">
            <div class="container p-0">
                <div class="bg-white dark:bg-grey-800 lg:mt-10">
                    <div class="grid grid-cols-10 rounded-sm">
                        {{ /* * */ }}
                        {{ /* * */ }}
                        {{ /* Sidebar  */ }}
                        {{ /* * */ }}
                        <div
                            class="col-span-10 lg:col-span-3 py-4 lg:py-8 lg:pb-24 lg:border-r border-black border-opacity-10 font-Inter-Regular h-full"
                            :class="!$store.state.checkout.cartItemsCount && 'opacity-40 pointer-events-none'"
                        >
                            <div
                                class="lg:sticky lg:top-[10.5rem] transition-all"
                                :class="isSidebarRepositioned && 'lg:top-[7.75rem]'"
                            >
                                <div
                                    class="flex items-center gap-x-4 lg:gap-x-9 px-4 lg:pl-10 lg:pr-4 mb-1 lg:mb-10 pt-4 lg:pt-0"
                                >
                                    <ProgressBar
                                        :current="$store.state.checkout.currentCheckoutStep"
                                        :total="steps.length"
                                    />
                                    <div class="flex flex-col">
                                        <span class="font-Inter-Bold block text-xl">{{ $t(steps[$store.state.checkout.currentCheckoutStep - 1]?.title || 'checkout.steps.payment') }}</span>
                                        <span
                                            v-if="$store.state.checkout.currentCheckoutStep < 4"
                                            class="block text-sm lg:text-base opacity-40 mt-1"
                                        >
                                            {{ steps[$store.state.checkout.currentCheckoutStep] && $t(steps[$store.state.checkout.currentCheckoutStep].title) }}&nbsp;<i
                                                class="ri-arrow-right-s-line"
                                            ></i
                                        ></span>
                                    </div>
                                </div>
                                <div class="hidden lg:flex flex-col mb-12">
                                    <div
                                        v-for="(step, i) in steps"
                                        :key="`step-${i}`"
                                        :class="
                                            $store.state.checkout.currentCheckoutStep === i + 1 && 'pointer-events-none'
                                        "
                                        @click="onSubmit(i + 1)"
                                    >
                                        <div
                                            class="px-4 lg:px-10 py-8 border-b border-black border-opacity-10 text-lg step-btn cursor-pointer hover:underline"
                                            :class="{
                                                active: $store.state.checkout.currentCheckoutStep === i + 1,
                                                done: $store.state.checkout.currentCheckoutStep > i + 1,
                                            }"
                                        >
                                            {{ $t(step.title) }}
                                        </div>
                                    </div>
                                </div>
                                <div v-if="$device.isDesktop" class="px-4 lg:px-10 mt-4 lg:mt-0 hidden lg:block">
                                    <span class="text-base lg:text-lg font-Inter-Bold">{{ $t('checkout.total') }}</span>
                                    <transition name="fade-in-value" mode="out-in">
                                        <div
                                            :key="total"
                                            class="text-2xl lg:text-3xl text-black dark:text-white block font-Inter-Semibold mt-1"
                                        >
                                            {{ total }}
                                        </div>
                                    </transition>
                                    <div
                                        v-if="$store.state.checkout_details.selected_shipping_method"
                                        class="flex flex-col gap-y-2"
                                    >
                                        <div class="flex items-center justify-between mt-3">
                                            <span class="block font-Inter-Bold mr-2 text-sm">{{ $t('checkout.shipping_fee') }}</span>
                                            <span class="block font-Inter-Regular text-sm">{{
                                                $formatCurrency(shippingPrice)
                                            }}</span>
                                        </div>
                                        <div class="flex items-center justify-between">
                                            <span class="block font-Inter-Bold mr-2 text-sm">{{ $t('checkout.products_price') }}</span>
                                            <span class="block font-Inter-Regular text-sm">{{
                                                $formatCurrency(cartTotalPrice)
                                            }}</span>
                                        </div>
                                    </div>

                                    <button
                                        v-if="!$auth.loggedIn"
                                        class="mt-7 login-btn group mb-4 lg:mb-20"
                                        @click="openAccountModal()"
                                    >
                                        <span class="whitespace-nowrap">{{ $t('checkout.already_registered') }}</span>
                                        <span class="font-Inter-Bold group-hover:underline">{{ $t('checkout.log_in') }}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div class="col-span-12 lg:col-span-7 pt-4 lg:py-10 px-4 lg:px-14 pb-12">
                            <Nuxt />
                            <transition name="page" mode="out-in">
                                <div
                                    v-if="
                                        ($device.isDesktop &&
                                            areCrlButtonsVisible &&
                                            $store.state.checkout.currentCheckoutStep >= 2) ||
                                        ($device.isMobile && $store.state.checkout.currentCheckoutStep >= 2)
                                    "
                                    class="flex items-center flex-col-reverse lg:flex-row lg:justify-between w-full mt-12 lg:mt-16 px-4 lg:pl-0 lg:pr-4 gap-y-4 lg:gap-y-0 lg:sticky lg:bottom-3 rounded-lg py-4 lg:py-3 lg:rounded-full z-10"
                                    :class="!isNavigationSticky && 'bg-white dark:bg-grey-800 box-shadow'"
                                >
                                    <div @click="$scrollToTop()">
                                        <PButton
                                            :to="prevStepUrl"
                                            appearance="secondary"
                                            :label="$t('checkout.nav.back')"
                                        />
                                    </div>
                                    <div class="w-full lg:w-auto" @click="$scrollToTop()">
                                        <div @click="onSubmit($store.state.checkout.currentCheckoutStep + 1)">
                                            <PButton
                                                :has-preloader="$store.state.checkout_details.is_loading"
                                                appearance="yellow"
                                                :disabled="
                                                    $store.state.checkout_details.selected_shipping_method === 2 &&
                                                    !$store.state.checkout_details.foxpost_point &&
                                                    $store.state.checkout.currentCheckoutStep === 3
                                                "
                                                class="w-full lg:w-auto min-w-[18rem]"
                                                :label="setNextStep.label"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </transition>
                        </div>
                    </div>
                </div>
            </div>
            <div ref="scrollTargetBottom"></div>
        </div>
        <Footer class="relative mt-14 lg:mt-36 z-[-1]" red />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import cart from '../pages/checkout/cart.vue';
import ProgressBar from '~/components/ProgressBar.vue';
import Footer from '~/components/Layout/Footer.vue';
import Header from '~/components/Layout/Header.vue';
import meta from '@/mixins/meta';

export default {
    name: 'Checkout',
    components: {
        Header,
        Footer,
        ProgressBar,
    },
    mixins: [meta],
    layout: 'default',
    data() {
        return {
            lastScroll: 0,
            isSidebarRepositioned: false,
            cartTotalPrice: 0,
            cartItems: null,
            steps: [
                { title: 'checkout.steps.cart', link: this.localePath('/rendeles/kosar') },
                { title: 'checkout.steps.personal_data', link: this.localePath('/rendeles/szemelyes-adatok') },
                { title: 'checkout.steps.shipping_payment', link: this.localePath('/rendeles/fizetes-es-kiszallitas') },
                { title: 'checkout.steps.summary', link: this.localePath('/rendeles/megrendeles-osszegzese') },
            ],
            prevStepUrl: null,
            areCrlButtonsVisible: false,
            isNavigationSticky: true,
            isNextBtnDisabled: true,
        };
    },
    async fetch() {
        try {
            // Run both requests concurrently
            const [cartRes, shippingRes] = await Promise.allSettled([
                this.$axios.get(`/cart/${this.$cookies.get('hsession')}`),
                this.$axios.get('/shipping'),
            ]);

            // Handle cart data
            if (cartRes.status === 'fulfilled' && cartRes.value?.data?.success === true) {
                this.cartTotalPrice = parseInt(cartRes.value.data?.sum) || 0;
                this.$store.commit('checkout/setCartItemsCount', cartRes.value.data.count);
                this.$store.commit('checkout/setCartTotal', this.cartTotalPrice);
            } else if (cartRes.status === 'rejected') {
                console.error('Error fetching cart data:', cartRes.reason);
            }

            // Handle shipping data
            if (shippingRes.status === 'fulfilled' && shippingRes.value?.data?.result === 'success' && shippingRes.value.data.data) {
                this.setShippingMethods(shippingRes.value.data.data);
            } else if (shippingRes.status === 'rejected') {
                console.error('Error fetching shipping data:', shippingRes.reason);
            }
        } catch (err) {
            console.error('Error in fetch():', err);
        }
    },
    computed: {
        cart() {
            return cart;
        },
        setNextStep() {
            if (this.$store.state.checkout.currentCheckoutStep === 2) {
                return {
                    label: this.$t('checkout.nav.to_payment_methods'),
                    path: this.localePath('/rendeles/fizetes-es-kiszallitas'),
                };
            } else if (this.$store.state.checkout.currentCheckoutStep === 3) {
                return { label: this.$t('checkout.nav.to_summary'), path: this.localePath('/rendeles/megrendeles-osszegzese') };
            } else {
                return {
                    label: this.$t('checkout.nav.to_payment'),
                    path: this.localePath({ path: 'rendeles-sikeres-rendeles', id: '123213321' }),
                };
            }
        },
        billingDetails() {
            return {
                aszf: this.$store.state.checkout_details.aszf,
                billing_address: this.$store.state.checkout_details.billing_address,
                billing_city: this.$store.state.checkout_details.billing_city,
                billing_first_name: this.$store.state.checkout_details.billing_first_name,
                billing_last_name: this.$store.state.checkout_details.billing_last_name,
                billing_postal_code: this.$store.state.checkout_details.billing_postal_code,
                email: this.$store.state.checkout_details.email,
                phone: this.$store.state.checkout_details.phone,
                shipping_address: this.$store.state.checkout_details.shipping_address,
                shipping_city: this.$store.state.checkout_details.shipping_city,
                shipping_first_name: this.$store.state.checkout_details.shipping_first_name,
                shipping_last_name: this.$store.state.checkout_details.shipping_last_name,
                shipping_postal_code: this.$store.state.checkout_details.shipping_postal_code,
                remark: this.$store.state.checkout_details.message,
            };
        },
        shippingPrice() {
            return this.$store.state.shipping_methods.find(
                (method) => method.id === this.$store.state.checkout_details.selected_shipping_method
            )?.cost;
        },
        total() {
            if (this.$store.state.checkout_details.selected_shipping_method) {
                return this.$formatCurrency(
                    parseInt(
                        this.cartTotalPrice +
                            this.$store.state.shipping_methods.find(
                                (method) => method.id === this.$store.state.checkout_details.selected_shipping_method
                            )?.cost || 0
                    )
                );
            } else {
                return this.$formatCurrency(parseInt(this.$store.state.checkout.cartTotal || this.cartTotalPrice));
            }
        },
    },

    watch: {
        '$route.path'(path) {
            if (path === '/rendeles/kosar') {
                this.setCheckoutStep(1);
            }
            if (path === '/rendeles/szemelyes-adatok') {
                this.setCheckoutStep(2);
                this.prevStepUrl = this.localePath('/rendeles/kosar');
            }
            if (path === '/rendeles/fizetes-es-kiszallitas') {
                this.setCheckoutStep(3);
                this.prevStepUrl = this.localePath('/rendeles/szemelyes-adatok');
            }
            if (path === '/rendeles/megrendeles-osszegzese') {
                this.setCheckoutStep(4);
                this.prevStepUrl = this.localePath('/rendeles/fizetes-es-kiszallitas');
            }
        },
        '$store.state.checkout.currentCheckoutStep'(value) {
            if (value !== 0) {
                setTimeout(() => {
                    this.areCrlButtonsVisible = true;
                }, 500);
            } else {
                this.areCrlButtonsVisible = false;
            }
        },
        '$store.state.checkout_details': {
            handler(value) {
                localStorage.setItem('checkoutDetails', JSON.stringify(value));

                if (value.selected_shipping_method !== 2) {
                    this.emptyFoxpostDetails();
                }

                // if (this.$store.state.checkout.currentCheckoutStep === 2) {
                //     if (value.first_name && value.last_name && value.zip_code) {
                //         this.isNextBtnDisabled = false;
                //     }
                // }
            },
            deep: true,
        },
        '$store.state.checkout.cartTotal'(value) {
            let total = value;

            if (!value) {
                total = 0;
            }

            this.cartTotalPrice = total;
        },
    },
    beforeMount() {
        window.removeEventListener('scroll', this.toggleStickySideBar);
    },
    mounted() {
        window.addEventListener('scroll', () => {
            this.toggleStickySideBar();
            this.handleStickyElements();
        });
        this.handleStickyElements();
        if (this.$route.path === '/rendeles/kosar') {
            this.setCheckoutStep(1);
        }
        if (this.$route.path === '/rendeles/szemelyes-adatok') {
            this.setCheckoutStep(2);
            this.prevStepUrl = this.localePath('/rendeles/kosar');
        }
        if (this.$route.path === '/rendeles/fizetes-es-kiszallitas') {
            this.setCheckoutStep(3);
            this.prevStepUrl = this.localePath('/rendeles/szemelyes-adatok');
        }
        if (this.$route.path === '/rendeles/megrendeles-osszegzese') {
            this.setCheckoutStep(4);
            this.prevStepUrl = this.localePath('/rendeles/fizetes-es-kiszallitas');
        }

        const storedCheckoutDetails = localStorage.getItem('checkoutDetails');

        if (storedCheckoutDetails) {
            const parsedDetails = JSON.parse(storedCheckoutDetails);
            this.updateCheckoutDetails(parsedDetails);
        }

        this.$store.state.checkout_details.is_loading = false;
    },
    beforeDestroy() {
        window.addEventListener('scroll', this.handleStickyElements);
    },
    methods: {
        ...mapActions([
            'setCheckoutErrors',
            'updateCheckoutDetails',
            'emptyCheckoutDetails',
            'emptyFoxpostDetails',
            'setShippingMethods',
        ]),
        async onSubmit(currentStep) {
            this.$store.state.checkout_details.is_loading = true;
            try {
                await this.$axios
                    .$post('/checkout', {
                        ...this.billingDetails,
                        payment_type: this.$store.state.checkout_details.selected_payment_method,
                        shipping_type: this.$store.state.checkout_details.selected_shipping_method,
                        foxpost_point: this.$store.state.checkout_details.foxpost_point,
                        session_id: this.$cookies.get('hsession'),
                        is_complete: currentStep === 5 ? 1 : 0,
                    })
                    .then((res) => {
                        this.$store.state.checkout_details.is_loading = false;

                        if (res.error) {
                            this.setCheckoutErrors(res.error);
                        }

                        if (currentStep === 1) {
                            this.$router.push(this.localePath('/rendeles/kosar'));
                        } else if (currentStep === 2) {
                            this.$router.push(this.localePath('/rendeles/szemelyes-adatok'));
                        } else if (currentStep === 3) {
                            if (
                                (res.success === false &&
                                    this.checkErrors(this.$store.state.checkout_errors, ['shipping_type'])) ||
                                this.checkErrors(this.$store.state.checkout_errors, ['is_complete'])
                            ) {
                                this.$router.push(this.localePath('/rendeles/fizetes-es-kiszallitas'));
                            }
                        } else if (
                            res.success === false &&
                            this.checkErrors(this.$store.state.checkout_errors, ['is_complete']) &&
                            currentStep === 4
                        ) {
                            this.$router.push(this.localePath('/rendeles/megrendeles-osszegzese'));
                        } else if (res.success === true && currentStep === 5) {
                            window.location.href = res?.paymentUrl;
                            (async () => {
                                await new Promise((resolve) => setTimeout(resolve, 250));
                                this.$store.commit('checkout/setCartItemsCount', 0);
                                this.emptyCheckoutDetails();
                            })();
                        }
                    });
            } catch (err) {
                this.$store.state.checkout_details.is_loading = false;
                console.log(err);
            }
        },
        setCheckoutStep(step) {
            this.$store.commit('checkout/setCurrentCheckoutStep', step);
            this.$scrollToTop();
        },
        toggleStickySideBar() {
            const scrollTop = window.scrollY;
            if (window.innerWidth > 1100 || this.$device.isDesktop) {
                this.isSidebarRepositioned = scrollTop >= this.lastScroll;
                this.lastScroll = window.scrollY;
            }
        },
        checkErrors(obj, keys) {
            const objKeys = Object.keys(obj);

            // Check if the number of keys matches the expected number
            if (objKeys.length !== keys.length) {
                return false;
            }

            // Check if all keys in the object match the expected keys
            return keys.every((key) => objKeys.includes(key));
        },
        openAccountModal() {
            this.$store.commit('globals/toggleLoginModal', true);
        },
        closeAccountModal() {
            this.$store.commit('globals/toggleLoginModal', false);
        },
        handleStickyElements() {
            const scrollTargetBottom = this.$refs.scrollTargetBottom;

            if (scrollTargetBottom) {
                const bottomElementRect = scrollTargetBottom.getBoundingClientRect();
                this.isNavigationSticky = bottomElementRect.bottom <= window.innerHeight;
            }
        },
    },
};
</script>

<style>
.remove-selected-product-btn {
    @apply w-3 h-3 block bg-center bg-no-repeat;
    background-size: 70%;
    background-image: url('~/assets/images/icons/close-icon-black.svg');
}

.step-btn {
    @apply gap-x-5 flex items-center;

    &::before {
        content: '';
        @apply h-5 w-5 min-w-[1.25rem] min-h-[1.25rem] min-h-[1.25rem] rounded-full border border-black border-opacity-50 block;
    }

    &.active {
        &::before {
            @apply bg-yellow-100 border-yellow-100;
        }
    }

    &.done {
        &::before {
            @apply bg-center bg-contain bg-black border border-black bg-no-repeat;
            background-size: 0.8rem;
            background-image: url('~/assets/images/icons/checkmark-icon-strong-white.svg');
        }
    }
}

.login-btn {
    @apply flex items-center gap-x-3;

    &::after {
        content: '';
        @apply w-5 h-5 bg-no-repeat bg-center block bg-contain;
        background-image: url('~/assets/images/icons/login-icon.svg');
    }
}

.radio-input {
    &__inner {
        @apply relative;

        &::before {
            content: '';
            @apply w-6 h-6 min-w-[1.5rem] min-h-[1.5rem] max-w-[1.5rem] max-h-[1.5rem] block rounded-full border border-black border-opacity-20;
        }

        &::after {
            content: '';
            @apply scale-0 transition-transform;
        }
    }

    &:checked {
        & + label {
            @apply border-opacity-100;

            .radio-input__inner {
                &::after {
                    content: '';
                    @apply w-4 h-4 min-w-[0.75rem] min-h-[0.75rem] max-w-[0.75rem] max-h-[0.75rem] block rounded-full bg-yellow-200 absolute left-[0.375rem] scale-100;
                }

                &::before {
                    @apply border-opacity-60;
                }
            }

            .input-checkmark {
                &::after {
                    @apply scale-100;
                }
            }
        }
    }

    & + label .input-checkmark {
        @apply w-6 h-6 min-w-[1.5rem] min-h-[1.5rem] max-w-[1.5rem] max-h-[1.5rem] block rounded-full border border-2 border-black relative flex items-center justify-center;

        &::after {
            content: '';
            @apply w-4 h-4 min-w-[0.75rem] min-h-[0.75rem] max-w-[0.75rem] max-h-[0.75rem] block rounded-full bg-yellow-200 absolute transition scale-0;
        }
    }
}

.is-valid {
    input {
        @apply border-green bg-green bg-opacity-[3%];
    }
}

.is-invalid {
    input {
        @apply border-error bg-error bg-opacity-[3%];
    }
}

.box-shadow {
    @media (min-width: 992px) {
        box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.1);
    }
}
</style>
